import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import API from "../../../util/API";
import useCookieConsent from "../../../hooks/useCookieConsent";
import { COOKIE_CONSENT, getOrCreatePersonalizationId, readRecentProductIds } from "../../../util/privacyConsent";
import { readRecommendationSearchTerms } from "../../../util/searchHistory";
import { getMarketplaceProductMeta } from "../products/MarketplaceProductCard";
import { consumePrefetch } from "../../../util/homePrefetch";

export const catalogRows = (data) => {
  const seen = new Set();
  return (Array.isArray(data) ? data : []).filter((product) => {
    const { id } = getMarketplaceProductMeta(product);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

export function recommendationRequest(allowed, country, currency) {
  const headers = { "x-personalization-consent": allowed ? "accepted" : "denied" };
  const params = { country, currency };
  if (allowed) {
    const visitor = getOrCreatePersonalizationId("cherrfy_visitor_id", "v");
    if (visitor) headers["x-visitor-key"] = visitor;
    const recent = readRecentProductIds();
    const searches = readRecommendationSearchTerms();
    if (recent.length) params.recentProductIds = recent.join(",");
    if (searches.length) params.searchTerms = searches.join("|");
  } else {
    // Anonymous recommendations must not attach an account token.
    headers.Authorization = "";
  }
  return { headers, params };
}

export default function useStorefront() {
  const country = useSelector((state) => state.userSettings?.shipToCountry) || "US";
  const currency = useSelector((state) => state.userSettings?.currency) || "USD";
  const token = useSelector((state) => state.userAuth?.token);
  // Token is read through a ref so a login does not refire the recommendation
  // effect on its own (a logged-in visitor reloading '/' already renders the
  // right sections); the null→token flip below triggers exactly one refetch.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const consent = useCookieConsent();
  const personalized = consent === COOKIE_CONSENT.ACCEPTED;
  const [refresh, setRefresh] = useState(0);
  const [visitSeed] = useState(() => Math.random().toString(36).slice(2) + Date.now().toString(36));
  const [variation, setVariation] = useState(0);
  const [catalog, setCatalog] = useState({ rows: [], page: 0, hasMore: false, loading: true, error: false });
  const [recommendations, setRecommendations] = useState({ picks: [], trending: [], collections: [], personalized: false, loading: true });
  const [categories, setCategories] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [page, setPage] = useState(1);
  const contextKey = country + "|" + currency;

  useEffect(() => {
    const controller = new AbortController();
    API.get("/category", { signal: controller.signal }).then(({ data }) => {
      const rows = Array.isArray(data) ? data : data?.data;
      setCategories((Array.isArray(rows) ? rows : []).filter((row) => row?.name).sort((a, b) => (a.position || 0) - (b.position || 0)));
    }).catch(() => {});
    API.get("/settings/hero-campaign", { signal: controller.signal }).then(({ data }) => setCampaign(data?.activeCampaign || null)).catch(() => {});
    return () => controller.abort();
  }, []);

  useEffect(() => { setPage(1); }, [contextKey]);
  useEffect(() => {
    const controller = new AbortController();
    // A changed market always starts at page one, even before the page reset renders.
    const requestedPage = catalog.contextKey === contextKey ? page : 1;
    setCatalog((current) => ({ ...current, loading: true, error: false }));
    const network = () => API.get("/product/", { params: { country, currency, page: requestedPage, limit: 12, paginationMode: "cursor", ...(requestedPage > 1 && catalog.nextCursor ? { cursor: catalog.nextCursor } : {}) }, signal: controller.signal, timeout: 20000 });
    // Boot prefetch (L1): page-1 rows were fetched before mount in global mode.
    const pre = requestedPage === 1 ? consumePrefetch("rows", country, currency) : null;
    const source = pre ? pre.then((data) => (data ? { data } : network())) : network();
    source
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        setCatalog((current) => ({
          contextKey, rows: catalogRows([...(requestedPage > 1 && current.contextKey === contextKey ? current.rows : []), ...catalogRows(data?.data)]),
          page: requestedPage, nextCursor: data?.pagination?.nextCursor, hasMore: Boolean(data?.pagination?.hasMore), loading: false, error: false,
        }));
      }).catch(() => {
        if (!controller.signal.aborted) setCatalog((current) => ({ ...current, loading: false, error: true }));
      });
    return () => controller.abort();
    // catalog is intentionally not a dependency: page and market own the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, currency, contextKey, page, refresh]);

  const recsControllerRef = useRef(null);

  const fetchRecs = () => {
    // A manual (login-flip) run can outlive the mounted effect run, so abort
    // the previous controller here rather than only in effect cleanup.
    if (recsControllerRef.current) recsControllerRef.current.abort();
    const controller = new AbortController();
    recsControllerRef.current = controller;
    const config = recommendationRequest(personalized, country, currency);
    if (personalized && tokenRef.current) {
      // Same bearer the API request interceptor attaches from storage; read
      // via ref so account-keyed requests reflect login without a dep.
      config.headers = { ...config.headers, Authorization: "Bearer " + tokenRef.current };
    }
    config.params.visitSeed = visitSeed + "_" + variation;
    setRecommendations({ contextKey, consent, picks: [], trending: [], collections: [], personalized: false, loading: true });
    let pending = 3;
    const preKind = { picks: "hero", trending: "trending", collections: "collections" };
    const load = (endpoint, key, limit) => {
      const network = () => API.get(endpoint, { ...config, params: { ...config.params, limit, ...(key === "picks" ? { includeCampaign: false } : {}) }, signal: controller.signal, timeout: 15000 });
      // Global-mode requests may reuse the boot prefetch (identical server-side
      // pool; visitSeed cannot change global ordering by design). Personalized
      // requests always fetch fresh.
      const pre = personalized ? null : consumePrefetch(preKind[key], country, currency);
      const source = pre ? pre.then((data) => (data ? { data } : network())) : network();
      return source
        .then(({ data }) => {
        if (controller.signal.aborted) return;
        setRecommendations((current) => ({ ...current,
          [key]: key === "collections" ? (Array.isArray(data?.data) ? data.data : []) : catalogRows(data?.data),
          ...(key === "picks" ? { personalized: personalized && Boolean(data?.algorithm?.personalized) } : {}),
        }));
      }).catch(() => {
        if (!controller.signal.aborted) setRecommendations((current) => ({ ...current, [key + "Error"]: true }));
      }).finally(() => {
        pending -= 1;
        if (!controller.signal.aborted && !pending) setRecommendations((current) => ({ ...current, loading: false }));
      });
    };
    // Render each section as soon as its response arrives.
    load("/product/hero", "picks", 12);
    load("/product/trending", "trending", 12);
    load("/product/collections", "collections", 3);
    return () => controller.abort();
  };

  const fetchRecsRef = useRef(fetchRecs);
  fetchRecsRef.current = fetchRecs;

  useEffect(() => {
    const abort = fetchRecs();
    return () => {
      abort();
      if (recsControllerRef.current) recsControllerRef.current.abort();
    };
    // token is intentionally not a dependency: it is read via tokenRef and the
    // login flip is handled by the one-time trigger below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, currency, contextKey, consent, personalized, refresh, visitSeed, variation]);

  const prevTokenRef = useRef(token);
  useEffect(() => {
    const previous = prevTokenRef.current;
    prevTokenRef.current = token;
    // null→token while mounted (login without a remount of this page): fetch
    // personalized recs once. Denied consent keeps the anonymous pool regardless
    // of login, so no refire is needed then.
    if (!previous && token && personalized) fetchRecsRef.current();
  }, [token, personalized]);

  const rows = useMemo(() => catalog.contextKey === contextKey ? catalog.rows : [], [catalog, contextKey]);
  const currentRecommendations = recommendations.contextKey === contextKey && recommendations.consent === consent ? recommendations : { picks: [], trending: [], collections: [], personalized: false, loading: true };
  return {
    country, currency, consent, personalized, categories, campaign, rows,
    catalogLoading: catalog.loading, catalogError: catalog.error, hasMore: catalog.hasMore,
    ...currentRecommendations,
    refreshPicks: () => setVariation((value) => value + 1),
    retry: () => setRefresh((value) => value + 1),
    loadMore: () => { if (!catalog.loading) setPage(catalog.page + 1); },
  };
}
